import React from 'react';
import { Link } from 'react-router-dom';
import LegalFooter from '../components/LegalFooter';

export default function Eligibility() {
  return (
    <div className="min-h-screen bg-white" data-testid="eligibility-page">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/tools" className="nav-link">Tools</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Eligibility Requirements
          </h1>
          <p className="text-sm text-zinc-500 mb-8">AI Accountability Standards Registry</p>
          
          <div className="prose prose-zinc max-w-none space-y-6 text-zinc-700">
            
            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">1. Business Entity Requirement</h2>
              <p>
                Accountability declaration is issued only to registered business entities. Applicants must be legally established organizations with verifiable business registration in their jurisdiction of operation.
              </p>
              <p className="mt-3 font-medium">
                Eligible entities include:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Corporations (C-Corp, S-Corp)</li>
                <li>Limited Liability Companies (LLC)</li>
                <li>Partnerships (LP, LLP)</li>
                <li>Registered business entities in equivalent foreign jurisdictions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">2. Ineligible Applicants</h2>
              <p>
                The following are not eligible for declaration:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Individual freelancers operating without business registration</li>
                <li>Informal operators without legal business status</li>
                <li>Sole proprietors without registered business entity</li>
                <li>Unincorporated associations</li>
                <li>Applicants unable to demonstrate legal business existence</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">3. Legal Business Existence</h2>
              <p>
                Applicants must demonstrate legal business existence through:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Valid business registration or incorporation documents</li>
                <li>Active status with relevant regulatory authorities</li>
                <li>Verifiable business address</li>
                <li>Good standing in jurisdiction of registration</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">4. Domain and Email Requirements</h2>
              <p>
                Applicants must provide:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>A business email address using an owned domain (not free email providers)</li>
                <li>Verifiable domain ownership or authorized use</li>
                <li>Working website at the specified domain</li>
              </ul>
              <p className="mt-3">
                Applications submitted with generic email addresses (gmail.com, yahoo.com, etc.) will not be processed.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">5. Accurate Representation</h2>
              <p>
                All information submitted in the declaration application must be accurate and complete. Applicants must not:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Misrepresent business status, history, or capabilities</li>
                <li>Provide false documentation</li>
                <li>Impersonate another organization</li>
                <li>Conceal material information relevant to eligibility</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">6. Consequences of Misrepresentation</h2>
              <p className="font-medium bg-zinc-100 p-4 border-l-4 border-zinc-400">
                False representation is grounds for immediate denial or revocation of declaration.
              </p>
              <p className="mt-4">
                Organizations found to have misrepresented eligibility information may be:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Denied declaration without refund</li>
                <li>Subject to declaration revocation</li>
                <li>Permanently barred from future applications</li>
                <li>Publicly recorded as having declaration withdrawn for cause</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">7. Verification Process</h2>
              <p>
                The Council reserves the right to verify eligibility information through:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Public business registration databases</li>
                <li>Domain ownership records</li>
                <li>Third-party verification services</li>
                <li>Direct inquiry to regulatory authorities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">8. Authority Reference</h2>
              <p>
                Eligibility requirements are established by the AI Accountability Standards Council. Complete policy documentation is available at <a href="https://aiaccountabilitycouncil.com" target="_blank" rel="noopener noreferrer" className="text-zinc-900 underline">aiaccountabilitycouncil.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Contact</h2>
              <p>
                Questions regarding eligibility requirements may be directed to{' '}
                <a href="mailto:contact@aiaccountabilitycouncil.com" className="text-zinc-900 underline">contact@aiaccountabilitycouncil.com</a>
                {' '}or through the{' '}
                <Link to="/contact" className="text-zinc-900 underline">contact form</Link>.
              </p>
            </section>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
